/**
 * «Загрузка 1С» (20.10.2026, владелец: «подготовь всё, чтобы можно было запустить за минуту»). Загрузчик архива 1С на
 * компьютере владельца показывает код (scripts/archive-1c/pair.mjs) — здесь его вводят и жмут «Разрешить» (окно с сетью и возрастом
 * запроса — POST /pair/check, 20.10 по совету основного чата; в окне — ещё раз «Разрешить»): загрузчик сам
 * получает узкий доступ (только загрузка 1С, от имени этого EO, 6 ч), выключатель загрузки открывается. «Закрыть доступ» —
 * отзывает все доступы загрузчика и закрывает выключатель. Сервер — apps/api/src/routes/archive-1c-pair (ROUTE-API.md §8).
 * Состояние: GET /api/eo/archive-1c/state (выключатель, ключ, схема, загрузки журнала) и /pair/state (запросы и доступы).
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { ApiException } from "../../api/types";
import { ConfirmModal } from "../../components/shared";
import { checkLines, codeInput, errText, hiddenPending, loadLines, offlineText, orenTime, summary, type A1cState, type PairCheck, type PairState } from "./a1cPanel";
import css from "./a1c.module.css";

const BASE = "/api/eo/archive-1c";
/* ответ сервера — по коду отказа; иначе обрыв связи (fetch бросил TypeError) — русским текстом, сырое сообщение — только в консоль */
const failText = (e: unknown, fallback: string) => {
  if (e instanceof ApiException) return errText(e.body, fallback);
  console.warn("archive-1c panel:", e);
  return offlineText(fallback);
};

export function Archive1cPanel() {
  const [st, setSt] = useState<A1cState | null>(null);
  const [pair, setPair] = useState<PairState | null>(null);
  const [err, setErr] = useState("");
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [ask, setAsk] = useState(false);
  /* окно «Разрешить»: ответ /pair/check вместе с кодом, по которому он получен, — одобряется ровно этот код (20.10, находка проверки) */
  const [chk, setChk] = useState<(PairCheck & { code: string; view: string }) | null>(null);

  const load = useCallback(() => {
    setErr("");
    void Promise.all([api<A1cState>(`${BASE}/state`), api<PairState>(`${BASE}/pair/state`)])
      .then(([s, p]) => { setSt(s); setPair(p); })
      .catch((e: unknown) => setErr(failText(e, "Не удалось загрузить состояние")));
  }, []);
  useEffect(load, [load]);

  const field = codeInput(raw);
  /* «Разрешить» → сначала что за запрос стоит за кодом (ничего не меняет), окно — и только там одобрение */
  const check = async () => {
    const { code, view } = field;
    if (!code || busy) return;
    setBusy(true); setToast("");
    try { setChk({ ...(await api<PairCheck>(`${BASE}/pair/check`, { method: "POST", body: { code } })), code, view }); }
    catch (e) { setToast(failText(e, "Не удалось проверить код")); }
    setBusy(false);
  };
  const approve = async () => {
    const code = chk?.code;
    setChk(null);
    if (!code) return;
    setBusy(true); setToast("");
    try {
      await api(`${BASE}/pair/approve`, { method: "POST", body: { code } });
      setRaw("");
      setToast("Разрешено. Загрузчик получит доступ в течение нескольких секунд; выключатель загрузки открыт.");
      load();
    } catch (e) { setToast(failText(e, "Не удалось разрешить")); }
    setBusy(false);
  };
  const close = async () => {
    setAsk(false); setBusy(true); setToast("");
    try {
      const r = await api<{ revoked: number }>(`${BASE}/pair/close`, { method: "POST", body: {} });
      setToast(`Доступ закрыт: отозвано ${r.revoked}, выключатель закрыт.`);
      load();
    } catch (e) { setToast(failText(e, "Не удалось закрыть доступ")); }
    setBusy(false);
  };

  const lines = summary(st, pair), loads = loadLines(st), hidden = hiddenPending(pair);
  return (
    <div className={css.wrap}>
      <div className={css.card}>
        <div className={css.head}>
          <span className={css.title}>Загрузка 1С</span>
          <button type="button" className={css.ghost} disabled={busy} onClick={load}>Обновить</button>
        </div>
        <p className={css.lead}>Загрузчик на компьютере показал код — введите его и нажмите «Разрешить». Загрузчик сам получит доступ:
          только к загрузке архива 1С, от вашего имени, на 6 часов. Код действует 10 минут.</p>
        <p className={css.warn}>Вводите только код с экрана своего загрузчика. Код, присланный кем-то в сообщении, не вводите.</p>
        <div className={css.codeRow}>
          <input className={css.code} value={field.view} placeholder="K7M-42X" aria-label="Код загрузчика" autoComplete="off" autoCorrect="off"
            autoCapitalize="characters" spellCheck={false} enterKeyHint="go" maxLength={12} disabled={busy || !!chk}
            onChange={(e) => setRaw(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void check(); }} />
          <button type="button" className={css.go} disabled={busy || !field.code} onClick={() => void check()}>Разрешить</button>
        </div>
        {toast ? <div className={css.toast} role="status">{toast}</div> : null}
        {err ? <div className={css.err}>{err}</div> : null}
        {!st && !err ? <div className={css.muted}>Загружаю состояние…</div> : null}
        {lines.length ? (
          <div className={css.grid}>
            {lines.map((l) => (
              <div key={l.label} className={css.cell}><span>{l.label}</span><b className={l.tone === "ok" ? css.ok : l.tone === "warn" ? css.hot : undefined}>{l.value}</b></div>
            ))}
          </div>
        ) : null}
        {pair?.active.length ? (
          <div className={css.block}>
            <div className={css.sub}>Доступы загрузчика</div>
            {pair.active.map((a) => (
              <div key={a.id} className={css.row}>{a.by_name ?? a.by ?? "—"} · {orenTime(a.issued_at)} → {orenTime(a.expires_at)}{a.net ? ` · ${a.net}` : ""}</div>
            ))}
          </div>
        ) : null}
        {pair?.pending.length ? (
          <div className={css.block}>
            <div className={css.sub}>Запросы кода</div>
            {pair.pending.map((p) => (
              <div key={p.id} className={css.row}>{p.status === "approved" ? "разрешён, ждёт загрузчик" : "ждёт кода"} · до {orenTime(p.expires_at)}{p.net ? ` · ${p.net}` : ""}</div>
            ))}
            {hidden ? <div className={css.muted}>и ещё {hidden} — коды, которые вы не запускали, не вводите; «Закрыть доступ» отзовёт все</div> : null}
          </div>
        ) : null}
        <div className={css.block}>
          <div className={css.sub}>Загрузки в журнале</div>
          {loads.length ? loads.map((l) => <div key={l} className={css.row}>{l}</div>) : <div className={css.muted}>{st?.journal.table ? "пока нет" : "журнала ещё нет"}</div>}
        </div>
        <div className={css.actions}>
          <button type="button" className={css.stop} disabled={busy} onClick={() => setAsk(true)}>Закрыть доступ</button>
        </div>
      </div>
      {chk ? (
        <ConfirmModal tone="warn" title={`Разрешить загрузчику доступ по коду ${chk.view}?`} actionLabel="Разрешить" actionDisabled={busy}
          onAction={() => void approve()} onCancel={() => setChk(null)}>
          {checkLines(chk).map((l) => <p key={l} className={css.modalLine}>{l}</p>)}
        </ConfirmModal>
      ) : null}
      {ask ? (
        <ConfirmModal tone="warn" title="Закрыть доступ загрузчика?" actionLabel="Закрыть" onAction={() => void close()} onCancel={() => setAsk(false)}>
          Все доступы загрузчика и ожидающие коды будут отозваны, выключатель загрузки закроется. Идущая загрузка остановится на следующей пачке.
        </ConfirmModal>
      ) : null}
    </div>
  );
}
